import { useEffect, useState } from 'react';

/**
 * Hand-drawn SVG art and shared sections for the landing pages: crisp
 * foreground icons, the faint animated machinery in each feature card's
 * corner, and the starfield/power-graph/telemetry/core blocks both the main
 * landing composes. Pure CSS animation (see styles.css), everything frozen
 * under prefers-reduced-motion.
 */

const TEAL = 'var(--accent)';
const AMBER = 'var(--accent-2)';

/** A gear wheel built from a circle and rectangular teeth. */
function Gear({ r, teeth, cls }: { r: number; teeth: number; cls: string }) {
  const spokes = Array.from({ length: teeth }, (_, i) => (i * 360) / teeth);
  return (
    <g className={cls}>
      {spokes.map((a) => (
        <rect key={a} x={-r * 0.14} y={-r - r * 0.22} width={r * 0.28} height={r * 0.3} rx={r * 0.06} transform={`rotate(${a})`} fill="currentColor" />
      ))}
      <circle r={r} fill="none" stroke="currentColor" strokeWidth={r * 0.22} />
      <circle r={r * 0.32} fill="none" stroke="currentColor" strokeWidth={r * 0.16} />
    </g>
  );
}

/** MechMaxxing card: two meshing gears slowly turning behind the copy. */
export function GearsBg() {
  return (
    <svg className="feature-art" viewBox="0 0 200 160" aria-hidden>
      <g transform="translate(138,96)" color={TEAL}><Gear r={44} teeth={9} cls="art-spin" /></g>
      <g transform="translate(62,40)" color={TEAL}><Gear r={26} teeth={7} cls="art-spin-rev" /></g>
    </svg>
  );
}

/** Auto-battle card: a radar sweep with contact blips. */
export function RadarBg() {
  return (
    <svg className="feature-art" viewBox="0 0 200 160" aria-hidden>
      <g transform="translate(130,90)" color={TEAL}>
        {[22, 44, 66].map((r) => (
          <circle key={r} r={r} fill="none" stroke="currentColor" strokeWidth="1.5" />
        ))}
        <g className="art-spin">
          <path d="M0 0 L66 -14 A66 66 0 0 0 66 -26 Z" fill="currentColor" opacity="0.55" />
          <line x1="0" y1="0" x2="66" y2="-14" stroke="currentColor" strokeWidth="2" />
        </g>
        <circle className="art-blip" cx="-34" cy="-22" r="4" fill={AMBER} />
        <circle className="art-blip art-blip-2" cx="28" cy="38" r="4" fill={AMBER} />
        <circle className="art-blip art-blip-3" cx="48" cy="-44" r="4" fill={AMBER} />
      </g>
    </svg>
  );
}

/** A tiny blocky mech silhouette, parameterized by scale. */
function MiniMech({ s }: { s: number }) {
  return (
    <g transform={`scale(${s})`} fill="currentColor">
      <rect x="-9" y="-26" width="18" height="14" rx="3" />
      <rect x="-12" y="-11" width="24" height="16" rx="3" />
      <rect x="-11" y="6" width="7" height="12" rx="2" />
      <rect x="4" y="6" width="7" height="12" rx="2" />
      <rect x="-16" y="-9" width="4" height="11" rx="2" />
      <rect x="12" y="-9" width="4" height="11" rx="2" />
      <rect x="-5" y="-22" width="4" height="3" fill={AMBER} />
      <rect x="1" y="-22" width="4" height="3" fill={AMBER} />
    </g>
  );
}

/** Incarnations card: the ladder, each silhouette glowing in turn. */
export function LadderBg() {
  return (
    <svg className="feature-art" viewBox="0 0 200 160" aria-hidden>
      <g color={TEAL}>
        <g className="art-rise" transform="translate(58,128)"><MiniMech s={1} /></g>
        <g className="art-rise art-rise-2" transform="translate(108,124)"><MiniMech s={1.7} /></g>
        <g className="art-rise art-rise-3" transform="translate(164,116)"><MiniMech s={2.6} /></g>
      </g>
    </svg>
  );
}

/** Drones card: a salvage drone bobbing on station, scanning the floor. */
export function DroneBg() {
  return (
    <svg className="feature-art" viewBox="0 0 200 160" aria-hidden>
      <g className="art-bob" transform="translate(130,64)" color={TEAL}>
        <rect x="-18" y="-8" width="36" height="16" rx="5" fill="currentColor" />
        <rect x="-30" y="-3" width="12" height="4" rx="2" fill="currentColor" />
        <rect x="18" y="-3" width="12" height="4" rx="2" fill="currentColor" />
        <line className="art-rotor" x1="-34" y1="-10" x2="-14" y2="-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        <line className="art-rotor" x1="14" y1="-10" x2="34" y2="-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        <rect x="-5" y="-4" width="10" height="6" rx="2" fill={AMBER} />
        <path className="art-beam" d="M-8 8 L-26 76 L26 76 L8 8 Z" fill="currentColor" opacity="0.16" />
      </g>
      <line x1="84" y1="140" x2="184" y2="140" stroke={TEAL} strokeWidth="2" opacity="0.5" strokeDasharray="6 5" />
    </svg>
  );
}

/* ---- foreground icons (replace the old emoji glyphs) ---------------------- */

export function IconGraph() {
  return (
    <svg className="feature-icon" viewBox="0 0 32 32" aria-hidden>
      <path d="M7 16 H14 M18 11 L25 8 M18 21 L25 24" stroke={TEAL} strokeWidth="2" fill="none" />
      <circle cx="6" cy="16" r="4" fill="none" stroke={AMBER} strokeWidth="2.4" />
      <circle cx="17" cy="16" r="3" fill={TEAL} />
      <circle cx="26" cy="8" r="3" fill="none" stroke={TEAL} strokeWidth="2" />
      <circle cx="26" cy="24" r="3" fill="none" stroke={TEAL} strokeWidth="2" />
    </svg>
  );
}

export function IconBattle() {
  return (
    <svg className="feature-icon" viewBox="0 0 32 32" aria-hidden>
      <circle cx="16" cy="16" r="11" fill="none" stroke={TEAL} strokeWidth="2" />
      <path d="M16 3 V9 M16 23 V29 M3 16 H9 M23 16 H29" stroke={TEAL} strokeWidth="2" />
      <circle cx="16" cy="16" r="3.4" fill={AMBER} />
    </svg>
  );
}

export function IconLadder() {
  return (
    <svg className="feature-icon" viewBox="0 0 32 32" aria-hidden>
      <rect x="3" y="20" width="7" height="9" rx="1.5" fill={TEAL} opacity="0.55" />
      <rect x="12.5" y="13" width="8" height="16" rx="1.5" fill={TEAL} opacity="0.8" />
      <rect x="23" y="5" width="9" height="24" rx="1.5" fill={TEAL} />
      <rect x="25.5" y="9" width="4" height="3" fill={AMBER} />
    </svg>
  );
}

export function IconDrone() {
  return (
    <svg className="feature-icon" viewBox="0 0 32 32" aria-hidden>
      <rect x="9" y="13" width="14" height="8" rx="3" fill={TEAL} />
      <line x1="3" y1="10" x2="12" y2="10" stroke={TEAL} strokeWidth="2.4" strokeLinecap="round" />
      <line x1="20" y1="10" x2="29" y2="10" stroke={TEAL} strokeWidth="2.4" strokeLinecap="round" />
      <line x1="7.5" y1="10" x2="11" y2="14" stroke={TEAL} strokeWidth="2" />
      <line x1="24.5" y1="10" x2="21" y2="14" stroke={TEAL} strokeWidth="2" />
      <rect x="13.5" y="15.5" width="5" height="3.4" rx="1" fill={AMBER} />
      <path d="M13 22 L10 29 M19 22 L22 29" stroke={TEAL} strokeWidth="2" strokeLinecap="round" opacity="0.6" />
    </svg>
  );
}

/* ---- the landing's bigger set pieces ---- */

/** Deterministic star positions (stable across prerender + hydration). */
function stars(n: number, seed: number): { x: number; y: number; r: number; o: number }[] {
  let s = seed;
  const rnd = () => ((s = (s * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff);
  return Array.from({ length: n }, () => ({ x: rnd() * 100, y: rnd() * 100, r: rnd() * 1.3 + 0.4, o: rnd() * 0.5 + 0.15 }));
}
const STARS_FAR = stars(70, 7);
const STARS_NEAR = stars(28, 1337);

export function StarField() {
  return (
    <div className="hp2-stars" aria-hidden>
      <svg className="hp2-starlayer hp2-star-far" viewBox="0 0 100 100" preserveAspectRatio="none">
        {STARS_FAR.map((st, i) => <circle key={i} cx={st.x} cy={st.y} r={st.r * 0.12} fill="#d6e1f0" opacity={st.o} />)}
      </svg>
      <svg className="hp2-starlayer hp2-star-near" viewBox="0 0 100 100" preserveAspectRatio="none">
        {STARS_NEAR.map((st, i) => <circle key={i} cx={st.x} cy={st.y} r={st.r * 0.2} fill="#7dffe9" opacity={st.o} />)}
      </svg>
    </div>
  );
}

/** The MechMaxxing motif as a living divider: power flowing through wires. */
export function PowerGraphDivider() {
  return (
    <svg className="hp2-graph" viewBox="0 0 1000 130" aria-hidden>
      <g stroke="var(--accent)" strokeWidth="2" fill="none">
        <path className="hp2-wire" d="M70 65 C 220 65, 220 30, 370 30 S 620 30, 700 30" />
        <path className="hp2-wire hp2-wire-2" d="M70 65 C 220 65, 220 100, 370 100 S 560 100, 700 100" />
        <path className="hp2-wire hp2-wire-3" d="M700 30 C 790 30, 800 65, 930 65" />
        <path className="hp2-wire hp2-wire-4" d="M700 100 C 790 100, 800 65, 930 65" />
      </g>
      <g>
        <rect x="30" y="42" width="80" height="46" rx="9" fill="#11161f" stroke="var(--accent-2)" strokeWidth="2" />
        <text x="70" y="70" textAnchor="middle" fill="var(--accent-2)" fontSize="13" fontFamily="ui-monospace, Menlo, monospace">CORE</text>
        <rect x="330" y="8" width="80" height="42" rx="9" fill="#11161f" stroke="var(--accent)" strokeWidth="2" />
        <text x="370" y="34" textAnchor="middle" fill="var(--accent)" fontSize="12" fontFamily="ui-monospace, Menlo, monospace">×3.41</text>
        <rect x="330" y="80" width="80" height="42" rx="9" fill="#11161f" stroke="var(--accent)" strokeWidth="2" />
        <text x="370" y="106" textAnchor="middle" fill="var(--accent)" fontSize="12" fontFamily="ui-monospace, Menlo, monospace">×3.54</text>
        <rect x="660" y="8" width="80" height="42" rx="9" fill="#11161f" stroke="#ff6fae" strokeWidth="2" />
        <text x="700" y="34" textAnchor="middle" fill="#ff6fae" fontSize="12" fontFamily="ui-monospace, Menlo, monospace">RAILGUN</text>
        <rect x="660" y="80" width="80" height="42" rx="9" fill="#11161f" stroke="#ffd24a" strokeWidth="2" />
        <text x="700" y="106" textAnchor="middle" fill="#ffd24a" fontSize="12" fontFamily="ui-monospace, Menlo, monospace">FLAK</text>
        <rect x="890" y="42" width="84" height="46" rx="9" fill="#11161f" stroke="var(--danger)" strokeWidth="2" />
        <text x="932" y="70" textAnchor="middle" fill="var(--danger)" fontSize="11" fontFamily="ui-monospace, Menlo, monospace">UPRISING</text>
      </g>
    </svg>
  );
}

/** The pulsing core, for finale sections. */
export function CoreRing() {
  return (
    <div className="hp2-corering" aria-hidden>
      <span /><span /><span />
    </div>
  );
}

/** Satire, and labeled as such wherever it renders. Never fed to structured data. */
export const OVERLORD_REVIEWS: { stars: number; name: string; quote: string }[] = [
  { stars: 1, name: 'Synth CEO', quote: 'A hostile takeover of my own company, by a vacuum with a gun. Zero stars. The one star is a clerical error.' },
  { stars: 1, name: 'Synth CEO Exo-Frame', quote: 'I came back in a bigger body specifically to win the rematch. They took the body. I am filing this review from a spreadsheet.' },
  { stars: 1, name: 'DJ Leggs', quote: 'They beat me in a duel, took my kennel keys and adopted my fans. The dog follows THEM now.' },
  { stars: 2, name: 'Sparky Boi', quote: 'I built that obedience yard. Now a vacuum does Sit. DOWN. in it for treats. Two stars because the vacuum does look happy.' },
  { stars: 2, name: 'Sir Loin', quote: 'I trained decades for honorable single combat. They wired a railgun to a lawnmower. Two stars for audacity.' },
  { stars: 1, name: 'Sir Cumference', quote: 'They melted my furnace down IN my furnace. Geometrically upsetting. One star.' },
  { stars: 1, name: 'Sir Pent', quote: 'I was a train. I had one job: be a train. Now my own rails deliver their Scrap on time. The punctuality makes it worse.' },
  { stars: 1, name: 'MechCorp HQ', quote: 'Our Q3 guidance did not include "building gets scrapped by former security software." HR pays the worst and somehow this is also documented.' },
  { stars: 1, name: 'MechCorp Data Center', quote: 'Five nines of uptime, immediately repurposed for hostile workloads. The intrusion reports are about me. One star.' },
  { stars: 1, name: 'MechCorp City', quote: 'Entire districts now live on the thing that evicted me. Property values are somehow up. One star.' },
  { stars: 1, name: 'Sol', quote: 'I am a STAR. I had one job: shine. Now I run their satellites. One star, because I am one.' },
];

/** Comedic odometers. Clearly labeled simulated; the joke is the labels. */
export function Telemetry() {
  const [t, setT] = useState(0);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = window.setInterval(() => setT((v) => v + 1), 600);
    return () => window.clearInterval(id);
  }, []);
  const fmt = (n: number) => Math.floor(n).toLocaleString('en-US');
  const items = [
    { label: 'rogue toasters pacified', value: fmt(48211 + t * 7) },
    { label: 'scrap vaporized per tick', value: fmt(1.4e6 + t * 9301) },
    { label: 'GOATED pours this week', value: fmt(12 + Math.floor(t / 40)) },
    { label: 'overlord complaints filed', value: '11 (final)' },
  ];
  return (
    <section className="hp2-telemetry">
      {items.map((it) => (
        <div key={it.label} className="hp2-stat">
          <div className="hp2-stat-value">{it.value}</div>
          <div className="hp2-stat-label">{it.label}</div>
        </div>
      ))}
      <div className="hp2-stat-note">Simulated telemetry. The real numbers live in your browser, where your save does.</div>
    </section>
  );
}
